import { AutoSaveIndicator } from '@dealstack/frontend/app/pages/Valuations/components/AutoSaveIndicator/AutoSaveIndicator';
import { ValuationsWarnings } from '@dealstack/frontend/app/pages/Valuations/components/ValuationsWarnings/ValuationsWarnings';
import {
	createValuationDetailsFormShape,
	defaultValuationDetailsFormValues,
} from '@dealstack/frontend/app/pages/Valuations/constants';
import { useValuationBreadcrumbs } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationBreadcrumbs';
import { useValuationNavigation } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationNavigation';
import { useValuationUpsert } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationUpsert';
import { useLatestFXRatesQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useLatestFXRatesQuery';
import { useHistoricalAndCustomFXRatesQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useHistoricalAndCustomFXRatesQuery';
import { useValuationCurrenciesQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useValuationCurrenciesQuery';
import { useValuationInputsQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useValuationInputsQuery';
import { getNumberFactor } from '@dealstack/frontend/app/pages/Valuations/shared/numberFactor';
import {
	NEW_VALUATION_ID,
	ValuationLocation,
} from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';
import {
	Container,
	TitleContainer,
	ValuationsLayout,
	ValuationsLoaderContainer,
} from '@dealstack/frontend/app/pages/Valuations/styled';
import { EmptyTargetNodes } from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/EmptyTargetNodes';
import { FXRatesDrawer } from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/FXRatesDrawer/FXRatesDrawer';
import { NewValuationPlaceholder } from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/NewValuationPlaceholder';
import { EditValuationContainer } from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/styled';
import { ValuationDetails } from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/ValuationDetails/ValuationDetails';
import { ValuationsInputWrapper } from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/ValuationsInputWrapper/ValuationsInputWrapper';
import { InPageNavValuationsHeaderContainer } from '@dealstack/frontend/app/pages/Valuations/views/ValuationsHome/styled';
import { getCapTableData } from '@dealstack/frontend/app/services/CapTableData.service';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { findCurrenciesWithoutRate } from '@dealstack/shared/lib/valuation/missingFxRates';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { DateTime } from 'luxon';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Flex, Grid, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { VALUATION_COMPUTE_START_QUERY_KEY } from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ValuationDetailsForm } from '@dealstack/frontend/app/pages/Valuations/constants.types';
import { uuidv7 } from 'uuidv7';

export const EditValuation = () => {
	const { dealId, pathValuationId, navigateTo } = useValuationNavigation();
	const tValuations = useTranslationValuations();
	const queryClient = useQueryClient();

	// Legacy URLs that land on /new (bookmarks, deep links) get rewritten to a
	// fresh client-generated id so the rest of the page can assume a stable
	// valuation id from mount onward.
	useEffect(() => {
		if (pathValuationId === NEW_VALUATION_ID) {
			navigateTo(ValuationLocation.SPECIFIC, {
				dealId: dealId ?? '',
				valuationId: uuidv7(),
			});
		}
	}, [pathValuationId, dealId, navigateTo]);

	const { currentValuation, isValuationsLoading, upsertValuation } = useValuationUpsert();

	const { currencies, refetchCurrencies } = useValuationCurrenciesQuery(
		dealId,
		currentValuation?.id
	);
	const { latestFxRates, isLatestFxRatesFetching, refetchLatestFXRates } = useLatestFXRatesQuery(
		currentValuation?.defaultCurrency,
		currencies
	);
	const {
		historicalAndCustomFXRates,
		isHistoricalAndCustomRatesFetching,
		refetchHistoricalAndCustomFXRates,
		updateCustomRates,
	} = useHistoricalAndCustomFXRatesQuery(
		dealId,
		currentValuation?.id,
		currentValuation?.defaultCurrency
	);

	const {
		inputs: valuationInputs,
		isLoading: isValuationInputsLoading,
		refetch: refetchInputs,
		convertAmount,
		saveFormSnapshot,
		flush: flushInputSaves,
		createInputAdjustment,
		deleteInputAdjustment,
	} = useValuationInputsQuery(dealId, currentValuation?.id, historicalAndCustomFXRates);

	const currentNumberFactor = getNumberFactor(currentValuation?.defaultNumberFormat);

	const previousValuationDate = useRef<string | undefined>(currentValuation?.valuationDate);
	useEffect(() => {
		if (
			previousValuationDate.current !== currentValuation?.valuationDate &&
			!!currentValuation?.valuationDate &&
			!!previousValuationDate.current
		) {
			void refetchInputs();
			void refetchHistoricalAndCustomFXRates();
		}
		previousValuationDate.current = currentValuation?.valuationDate;
	}, [currentValuation?.valuationDate, refetchInputs, refetchHistoricalAndCustomFXRates]);

	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);
	const getCapTableDataRequest = useQuery({
		queryKey: [
			API_PATHS.CAP_DATA_POST,
			{
				body: {
					dealId: dealId ?? '',
				},
			},
		],
		queryFn: () =>
			getCapTableData(API_PATHS.CAP_DATA_POST, {
				body: {},
			}),
	});

	useValuationBreadcrumbs(
		{
			view: 'edit',
			dealId: dealId ?? '',
			valuation: currentValuation ?? undefined,
		},
		inPageFeatureFlag
	);

	const valuationDetailsForm = useForm<ValuationDetailsForm>({
		mode: 'onChange',
		resolver: zodResolver(createValuationDetailsFormShape),
		defaultValues: defaultValuationDetailsFormValues,
	});

	const [isFXDrawerOpen, setIsFXDrawerOpen] = useState(false);
	const [isRunningValuation, setIsRunningValuation] = useState(false);

	const onCloseFXDrawer = useCallback(() => {
		setIsFXDrawerOpen(false);
	}, []);

	const openFXDrawer = useCallback(() => {
		void refetchLatestFXRates();
		setIsFXDrawerOpen(true);
	}, [refetchLatestFXRates]);

	const runValuation = useCallback(async () => {
		if (!currentValuation || isRunningValuation) {
			return;
		}

		setIsRunningValuation(true);
		try {
			const [valuationSaved, inputsSaved] = await Promise.all([
				upsertValuation.flush(),
				flushInputSaves(),
			]);
			if (!valuationSaved || !inputsSaved) {
				return;
			}

			// A currency with no rate would be valued 1:1 with the base, so the run waits for one.
			const [{ data: currenciesRes }, { data: ratesRes }] = await Promise.all([
				refetchCurrencies(),
				refetchHistoricalAndCustomFXRates(),
			]);
			const fx = ratesRes?.payload;
			const unrated = fx
				? findCurrenciesWithoutRate(
						fx.base,
						currenciesRes?.payload?.currencies ?? [],
						fx.rates
					)
				: [];
			if (fx && unrated.length > 0) {
				showToastV2({
					state: 'error',
					message: tValuations('run_new_valuation.missing_fx_rates', {
						currencies: unrated.join(', '),
						date: DateTime.fromISO(fx.date, { zone: 'utc' }).toFormat('dd/MM/yyyy'),
					}),
				});
				openFXDrawer();
				return;
			}

			// The summary view's compute-start query is cached per (dealId, valuationId)
			// with staleTime: Infinity (WAT-730), so navigating back to the same
			// valuation would otherwise reuse the already-resolved compute instead of
			// running a new one. Invalidate it so the summary view mints a fresh workflow.
			await queryClient.invalidateQueries({
				queryKey: [VALUATION_COMPUTE_START_QUERY_KEY, dealId ?? '', currentValuation.id],
			});
			navigateTo(ValuationLocation.SUMMARY, {
				dealId: dealId ?? '',
				valuationId: currentValuation.id,
			});
		} finally {
			setIsRunningValuation(false);
		}
	}, [
		currentValuation,
		isRunningValuation,
		upsertValuation,
		flushInputSaves,
		refetchCurrencies,
		refetchHistoricalAndCustomFXRates,
		tValuations,
		openFXDrawer,
		queryClient,
		dealId,
		navigateTo,
	]);

	// Gate the form on the cap-table eligibility query too, else the form
	// mounts and flashes for one render before EmptyTargetNodes swaps in
	// once the query resolves with zero issuers (WAT-713).
	if (
		getCapTableDataRequest.isLoading ||
		(isValuationsLoading && pathValuationId !== undefined)
	) {
		return (
			<ValuationsLoaderContainer>
				<InlineLoader withoutBackground />
			</ValuationsLoaderContainer>
		);
	}

	if (getCapTableDataRequest.data?.issuers.length === 0) {
		return <EmptyTargetNodes />;
	}

	return (
		<ValuationsLayout>
			<InPageNavValuationsHeaderContainer>
				<TitleContainer>
					<Text size="B3Bold" c="dark.9">
						{currentValuation?.name}
					</Text>
					<AutoSaveIndicator />
				</TitleContainer>
				<Flex align="center" gap="xs">
					<Button
						onClick={runValuation}
						// Never disabled while a save is pending/in flight: that gate flips on
						// asynchronously (autosave debounce) and drops clicks that arrive as it
						// fires - the summary-page e2e flake. runValuation flushes saves instead.
						disabled={!currentValuation || isRunningValuation}
						loading={isRunningValuation}
						data-testid={TEST_ELEMENT_IDS.VALUATION_EDIT_RUN_NEW_VALUATION_BUTTON}
						rightSection={<Icon variant="arrow-right" size="lg" />}
					>
						{tValuations('run_new_valuation.button')}
					</Button>
					<ValuationsWarnings />
				</Flex>
			</InPageNavValuationsHeaderContainer>
			<Container>
				<EditValuationContainer p="1rem" fluid>
					<Grid gutter="1rem" grow>
						<Grid.Col span={12}>
							<FormProvider {...valuationDetailsForm}>
								<ValuationDetails
									key={pathValuationId}
									onFXRatesClick={openFXDrawer}
									currentValuation={currentValuation}
									upsertValuation={upsertValuation}
								/>
							</FormProvider>
						</Grid.Col>

						{pathValuationId !== NEW_VALUATION_ID &&
							currentValuation &&
							!isValuationInputsLoading && (
								<ValuationsInputWrapper
									valuationInputsRaw={valuationInputs}
									convertAmount={convertAmount}
									currentNumberFactor={currentNumberFactor}
									valuationDate={currentValuation?.valuationDate}
									defaultCurrency={currentValuation?.defaultCurrency}
									defaultNumberFormat={currentValuation?.defaultNumberFormat}
									saveFormSnapshot={saveFormSnapshot}
									createInputAdjustment={createInputAdjustment.mutateAsync}
									deleteInputAdjustment={deleteInputAdjustment.mutate}
								/>
							)}
					</Grid>

					{(pathValuationId === NEW_VALUATION_ID || !currentValuation) && (
						<Flex direction="column" style={{ flex: 1, minHeight: '24rem' }}>
							<NewValuationPlaceholder />
						</Flex>
					)}
				</EditValuationContainer>
			</Container>
			<FXRatesDrawer
				opened={isFXDrawerOpen}
				onClose={onCloseFXDrawer}
				control={valuationDetailsForm.control}
				currentValuation={currentValuation}
				currencies={currencies}
				latestFxRates={latestFxRates}
				historicalAndCustomFXRates={historicalAndCustomFXRates}
				updateCustomRates={updateCustomRates}
				isHistoricalAndCustomRatesFetching={isHistoricalAndCustomRatesFetching}
				isLatestFxRatesFetching={isLatestFxRatesFetching}
				refetchCurrencies={refetchCurrencies}
			/>
		</ValuationsLayout>
	);
};
